import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatApiError } from "../lib/api";
import { Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "../components/BrandLogo";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, logout } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user?.role !== "admin") {
        await logout();
        setErr("This login is for administrators only. Please use the main sign-in page.");
        return;
      }
      toast.success("Admin session started");
      nav("/admin/listings");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2" data-testid="admin-login-page">
      <div className="hidden lg:block relative bg-black text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-10" />
        <div className="relative p-12 h-full flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo size="lg" />
            <span className="font-display text-2xl font-bold">TradeFlow UK</span>
          </Link>
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#FFCC00] mb-3 flex items-center gap-2">
              <ShieldAlert className="w-3.5 h-3.5" /> Restricted area
            </p>
            <h2 className="font-display text-4xl font-bold leading-tight">
              Moderation console.<br />
              <span className="bg-[#FFCC00] text-black px-2">Admins only.</span>
            </h2>
            <p className="mt-4 text-sm text-zinc-400 max-w-sm leading-relaxed">
              Manage business listings and offers, and take down anything unsuitable.
              Business accounts should use the main sign-in page.
            </p>
          </div>
          <p className="text-sm text-zinc-400">© {new Date().getFullYear()} · Built in Britain</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-black rounded-full mb-4 bg-white">
            <ShieldAlert className="w-3 h-3" />
            <span className="text-[10px] font-mono uppercase tracking-widest">Admin sign in</span>
          </div>
          <h1 className="font-display text-3xl font-bold mb-2">Staff access.</h1>
          <p className="text-zinc-600 mb-8">Enter your admin credentials to reach the moderation console.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
            <div>
              <Label htmlFor="email">Admin email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="mt-1"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-zinc-600 hover:text-black underline"
                  data-testid="admin-link-forgot-password"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                className="mt-1"
              />
            </div>
            {err && <p className="text-sm text-red-600" data-testid="admin-login-error">{err}</p>}
            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full bg-black hover:bg-zinc-800 text-white rounded-md py-6 font-semibold"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in to admin console"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-zinc-600">
            Not an admin?{" "}
            <Link to="/login" className="font-medium text-black underline hover:no-underline" data-testid="link-trader-login">
              Business sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
